import type { LucideIcon } from 'lucide-react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn, formatNumber, formatPercentage } from '@/lib/utils';

interface StatCardProps {
  title: string;
  value: number | string;
  change?: number;
  changeLabel?: string;
  icon: LucideIcon;
  format?: 'number' | 'percentage' | 'none';
}

export function StatCard({
  title,
  value,
  change,
  changeLabel = 'vs last period',
  icon: Icon,
  format = 'number',
}: StatCardProps) {
  const formattedValue =
    format === 'number' && typeof value === 'number'
      ? formatNumber(value)
      : format === 'percentage' && typeof value === 'number'
        ? formatPercentage(value)
        : value;

  const isPositiveChange = change !== undefined && change >= 0;

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="font-display text-3xl font-bold text-foreground">{formattedValue}</p>
            {change !== undefined && (
              <div className="flex items-center gap-1 text-sm">
                {isPositiveChange ? (
                  <TrendingUp className="h-4 w-4 text-orb-teal" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-destructive" />
                )}
                <span className={cn(isPositiveChange ? 'text-orb-teal' : 'text-destructive')}>
                  {formatPercentage(Math.abs(change))}
                </span>
                <span className="text-muted-foreground">{changeLabel}</span>
              </div>
            )}
          </div>
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orb-gold/10">
            <Icon className="h-6 w-6 text-orb-gold" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
